/**
 * SOC 2 program panels on the objective engine — V106-1-8 (ADR-0078; design pass
 * `v106-program-screens` round 3, `soc2.html`).
 *
 *   Scope    Trust Services Categories (Security always in scope) with a pending
 *            bar on any change: before → after criteria / points, what joins or
 *            leaves (evidence is kept), the effect on the open window, and a
 *            required "Applies to" (this window — record that the CPA agreed —
 *            or from the next window, which keeps the open window's scope);
 *            Save / Cancel; Undo restores the previous scope (H5, H28, H29).
 *   Prepare  the switch acknowledgement (D6) above the points-of-focus Prepare
 *            (ObjectivePrepare): owners, admins and the SOC 2 lead see it until
 *            one of them clicks "Got it" (the first one is recorded); everyone
 *            else sees a one-line notice for 14 days.
 *   Audit    the period switcher (default: the period with an open customer
 *            action); a window still open (fieldwork, expected report date with
 *            Change); an ended one: the issued report — attach the PDF (a file is
 *            required; item 3), the opinion and date from the CPA, read-only CPA
 *            results; off-platform CPAs: record the issued report (opinion, date,
 *            PDF) shown as entered by your team.
 *   Operate  FedRAMP Class A material from SOC 2 (only when the workspace runs
 *            Class A): linked / 1 item missing / expiring / expired / opinion not
 *            unmodified, with the upload for the missing CPA schedule.
 *
 * Before the `soc2-readiness` switch (508) every tab keeps today's framework
 * view (program-panels.tsx decides); Scope's categories work in both.
 *
 * Reads (any member): the screen aggregate, objective readiness, determinations,
 * members. Writes (server-enforced): scope options (owner/admin or the SOC 2
 * lead), program entries, the SOC 2 report upload (owner/admin or the lead),
 * Class A materials (owner/admin or the FedRAMP lead).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Field, Input, Select, Spinner, Switch } from '@aigovops/design-system';
import { ApiError, getObjectiveReadiness, listDeterminations, listWorkspaceMembers, type ProgramCard } from '../../lib/api';
import { queryKeys } from '../../lib/query-keys';
import { uploadAlternativeMaterial } from '../../lib/govramp-api';
import {
  endProgramEntry, getProgramScreen, recordProgramEntry, setScopeOptions, uploadSoc2Report, type ProgramScreen,
} from '../../lib/program-screens-api';
import { SOC2_CATEGORIES, buildTree, dayDiff, fmtDate, objectiveAnchor, relDays } from '../../lib/program-screens-model';
import { useAnnounce } from '../today/shared';
import { Datebox, Glyph } from './govramp-parts';
import { ActButton, AskButton, FileField, Gate, focusWhenReady, type Person } from './objective-parts';
import { ObjectivePrepare } from './ObjectivePrepare';
import '../../pages/govramp.css';
import '../../pages/today.css';
import '../../pages/program-screens.css';

const today = () => new Date().toISOString().slice(0, 10);
const errText = (e: unknown, fallback: string) => (e instanceof ApiError ? ((e.body as { error?: string; detail?: string } | undefined)?.error ?? e.message) : e instanceof Error ? e.message : fallback);
export const screenKey = (slug: string, program: string) => ['program-screen', slug, program] as const;

export function useProgramScreen(slug: string, program: string) {
  return useQuery({ queryKey: screenKey(slug, program), queryFn: () => getProgramScreen(slug, program), retry: false, staleTime: 30_000 });
}

export function usePeople(slug: string): { people: Person[]; byId: (id: string | null | undefined) => Person | null } {
  const q = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000 });
  const people = useMemo(() => (q.data?.members ?? []).map((m) => ({ user_id: m.user_id, name: m.display_name || m.email, email: m.email ?? null, role: m.role })), [q.data]);
  return { people, byId: (id) => people.find((p) => p.user_id === id) ?? null };
}

/** The open period: not closed, not ended, ending soonest; else the latest not closed. */
export function openPeriodOf(screen: ProgramScreen) {
  const t = today();
  const live = screen.periods.filter((p) => p.status !== 'closed').sort((a, b) => a.end.localeCompare(b.end));
  return live.find((p) => p.end >= t) ?? live.at(-1) ?? null;
}

const OPINION: Record<string, string> = { unmodified: 'Unmodified', qualified: 'Qualified', adverse: 'Adverse', disclaimer: 'Disclaimer of opinion' };
const firstName = (n: string) => n.split(/\s+/)[0] || n;

export interface Soc2PanelProps {
  slug: string;
  prefix: string;
  card: ProgramCard;
  canManage: boolean;
  isAdmin: boolean;
  viewerId: string;
  role: string;
  tabHref: (t: 'scope' | 'prepare' | 'audit' | 'operate' | 'report') => string;
}

// ---- Scope: Trust Services Categories ------------------------------------------------------

const SECURITY = { criteria: 33, points: 208 };
const CAT_COUNTS: Record<string, { criteria: number; points: number }> = {
  availability: { criteria: 3, points: 17 }, confidentiality: { criteria: 2, points: 12 }, processing_integrity: { criteria: 5, points: 19 }, privacy: { criteria: 18, points: 74 },
};
const PROCESSOR_DROP = 15;
const ROLE_LABEL: Record<string, string> = { both: 'Controller and processor', controller: 'Controller', processor: 'Processor' };

export function Soc2Categories({ slug, prefix, card, canManage, viewerId, screen }: Soc2PanelProps & { screen: ProgramScreen }) {
  const qc = useQueryClient();
  const [announceNode, announce] = useAnnounce();
  const { byId } = usePeople(slug);
  const saved = (screen.options.categories as string[] | undefined) ?? [];
  const savedRole = (screen.options.privacy_role as string | undefined) ?? 'both';
  const [cats, setCats] = useState<string[]>(saved);
  const [role, setRole] = useState(savedRole);
  const [applies, setApplies] = useState<'current' | 'next' | null>(null);
  const [appliesErr, setAppliesErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ msg: string; prev: { categories: string[]; privacy_role: string }; first: string; agreement: string | null; applies: 'current' | 'next' } | null>(null);
  const lastSwitch = useRef<string | null>(null);
  const switchRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const bar = useRef<HTMLDivElement>(null);
  const undoRef = useRef<HTMLButtonElement>(null);
  const open = openPeriodOf(screen);
  const readiness = useQuery({ queryKey: queryKeys.objectiveReadiness(slug, 'soc2', open?.period_id ?? ''), queryFn: () => getObjectiveReadiness(slug, 'soc2', open!.period_id), enabled: !!open && screen.engine?.engine === 'objective', retry: false, staleTime: 60_000 });
  const tree = readiness.data ? buildTree(readiness.data.readiness) : null;
  const total = (list: string[], r: string) => list.reduce((a, k) => ({ criteria: a.criteria + (CAT_COUNTS[k]?.criteria ?? 0), points: a.points + (CAT_COUNTS[k]?.points ?? 0) - (k === 'privacy' && r === 'processor' ? PROCESSOR_DROP : 0) }), { ...SECURITY });
  // Aesthetic H (round 2): the in-scope total is the server's (the same number Prepare shows); the catalogue
  // counts only preview the change a pending switch makes.
  const est = { before: total(saved, savedRole), after: total(cats, role) };
  const before = tree ? { criteria: tree.criteria.total, points: tree.counts.total } : est.before;
  const after = { criteria: before.criteria + est.after.criteria - est.before.criteria, points: before.points + est.after.points - est.before.points };
  const added = cats.filter((k) => !saved.includes(k));
  const removed = saved.filter((k) => !cats.includes(k));
  const roleChanged = cats.includes('privacy') && saved.includes('privacy') && role !== savedRole;
  const pending = added.length + removed.length > 0 || roleChanged;
  const coveredIn = (k: string) => {
    const series = SOC2_CATEGORIES.find((c) => c.key === k)?.series ?? [];
    return tree ? tree.groups.filter((g) => (series as readonly string[]).includes(g.group)).reduce((n, g) => n + g.criteria.reduce((m, c) => m + c.covered, 0), 0) : 0;
  };
  const coveredLeaving = removed.reduce((n, k) => n + coveredIn(k), 0);
  const firm = open?.soc2?.firm_name ?? 'your CPA';
  useEffect(() => { if (pending) bar.current?.scrollIntoView?.({ block: 'nearest' }); }, [pending, cats, role, applies]);
  useEffect(() => { if (removed.length && applies === 'current') setApplies(null); }, [removed.length, applies]);

  const label = (k: string) => SOC2_CATEGORIES.find((c) => c.key === k)?.label ?? k;
  const changes = [
    ...added.map((k) => `${label(k)} joins · ${CAT_COUNTS[k]!.points - (k === 'privacy' && role === 'processor' ? PROCESSOR_DROP : 0)} points start with no evidence`),
    ...removed.map((k) => `${label(k)} leaves · ${CAT_COUNTS[k]!.points} points leave scope${tree ? ` (${coveredIn(k)} of them covered)` : ''} · evidence is kept`),
    ...(roleChanged ? [`Privacy role ${ROLE_LABEL[savedRole]} → ${ROLE_LABEL[role]}`] : []),
  ];
  const coveredNow = tree?.counts.covered ?? null;
  const verdict = !applies ? 'Choose a window to see the effect'
    : applies === 'next' ? `${open?.label ?? 'The open window'} keeps its current scope · the next window starts with this one`
      : coveredNow !== null ? `${open?.label ?? 'The open window'} becomes ${coveredNow - coveredLeaving} of ${after.points} covered (${Math.round(((coveredNow - coveredLeaving) / Math.max(1, after.points)) * 100)}%)` : 'Readiness changes now';

  const onSwitch = (k: string, on: boolean) => { lastSwitch.current = k; setDone(null); setCats((c) => (on ? [...c, k] : c.filter((x) => x !== k))); announce(`Scope change pending · ${label(k)} ${on ? 'joins' : 'leaves'}`); };
  const cancel = () => { setCats(saved); setRole(savedRole); setApplies(null); setAppliesErr(false); announce('Scope change cancelled'); window.setTimeout(() => switchRefs.current[lastSwitch.current ?? '']?.focus(), 0); };
  const save = async () => {
    if (busy) return;
    if (!applies) { setAppliesErr(true); announce('Choose which window this applies to'); document.querySelector<HTMLInputElement>('input[name="applies"]:not(:disabled)')?.focus(); return; }
    setBusy(true); setErr(null);
    try {
      await setScopeOptions(slug, card.profile_id, { categories: cats, privacy_role: cats.includes('privacy') ? role : null }, applies);
      let agreement: string | null = null;
      if (applies === 'current' && open) {
        const e = await recordProgramEntry(slug, 'soc2', { kind: 'scope_change_agreement', subject_key: open.period_id, data: { firm, agreed_on: today(), categories: cats } });
        agreement = e.entry_id;
      }
      const msg = `Scope saved · ${applies === 'next' ? 'from the next window' : `${open?.label ?? 'this'} window`} · ${after.criteria} criteria · ${after.points} points of focus`;
      setDone({ msg, prev: { categories: saved, privacy_role: savedRole }, first: lastSwitch.current ?? added[0] ?? removed[0] ?? '', agreement, applies });
      setApplies(null);
      announce(msg);
      await qc.invalidateQueries({ queryKey: screenKey(slug, 'soc2') });
      void qc.invalidateQueries({ queryKey: ['objective-readiness', slug] });
      focusWhenReady(() => undoRef.current, () => document.getElementById('cat-h'));
    } catch (e) { setErr(errText(e, 'The scope could not be saved.')); } finally { setBusy(false); }
  };
  const undo = async () => {
    if (busy) return;
    if (!done) return;
    setBusy(true);
    try {
      await setScopeOptions(slug, card.profile_id, { categories: done.prev.categories, privacy_role: done.prev.categories.includes('privacy') ? done.prev.privacy_role : null }, done.applies);
      if (done.agreement) await endProgramEntry(slug, 'soc2', done.agreement, 'undo');
      setCats(done.prev.categories); setRole(done.prev.privacy_role);
      const first = done.first;
      setDone(null);
      announce(`Scope change undone · ${total(done.prev.categories, done.prev.privacy_role).criteria} criteria · ${total(done.prev.categories, done.prev.privacy_role).points} points of focus`);
      await qc.invalidateQueries({ queryKey: screenKey(slug, 'soc2') });
      focusWhenReady(() => switchRefs.current[first], () => document.getElementById('cat-h'));
    } catch (e) { setErr(errText(e, 'The change could not be undone.')); } finally { setBusy(false); }
  };
  const lead = screen.lead ? byId(screen.lead.user_id) : null;

  return (
    <section className="gr-card" aria-labelledby="cat-h" data-part="soc2-categories">
      {announceNode}
      <div className="gr-card-head">
        <h3 id="cat-h" className="gr-card-h" tabIndex={-1}>Trust Services Categories</h3>
        
      </div>
      <div>
        <div className="v6-cat">
          <div><div className="v6-cat-n">Security</div><div className="v6-cat-m">{`${SECURITY.criteria} criteria · ${SECURITY.points} points of focus`}</div></div>
          <span className="v6-fixed"><span className="gr-g gr-g--done" aria-hidden="true">✓</span>Always in scope</span>
        </div>
        {SOC2_CATEGORIES.map((c) => {
          const on = cats.includes(c.key);
          const n = CAT_COUNTS[c.key]!;
          return (
            <div className="v6-cat" key={c.key}>
              <div><div className="v6-cat-n" id={`cn-${c.key}`}>{c.label}</div><div className="v6-cat-m">{`${saved.includes(c.key) ? '' : 'Adds '}${n.criteria} criteria · ${n.points} points of focus`}</div></div>
              <Switch ref={(el) => { switchRefs.current[c.key] = el; }} checked={on} label={c.label} onText="In" offText="Out" disabled={!canManage || busy}
                aria-describedby={!canManage ? 'cat-gate' : undefined} onChange={(v) => onSwitch(c.key, v)} />
              {c.key === 'privacy' && on ? (
                <div className="v6-cat-x"><Field id="pr-role" label="Your role for personal data"><Select value={role} disabled={!canManage} onChange={(e) => { setRole(e.currentTarget.value); setDone(null); }}>
                  {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field></div>
              ) : null}
            </div>
          );
        })}
      </div>
      {!canManage ? <Gate id="cat-gate" who="The SOC 2 lead or an admin" verb="changes the categories"
        ask={lead && lead.user_id !== viewerId ? <AskButton slug={slug} program="soc2" person={lead} about="Change the SOC 2 categories" announce={announce} /> : null} /> : null}
      <p className="v6-total"><b>{before.criteria}</b><span className="gr-dim">criteria</span><b>{before.points}</b><span className="gr-dim">points of focus in scope</span></p>

      {pending && canManage ? (
        <div className="v6-pending" ref={bar} role="group" aria-labelledby="pend-h" data-part="scope-pending">
          <p id="pend-h" className="gr-strong">Scope change · not saved</p>
          <p className="gr-small"><b>{`${before.criteria} → ${after.criteria}`}</b> criteria · <b>{`${before.points} → ${after.points}`}</b> points of focus</p>
          <ul className="gr-small">{changes.map((x) => <li key={x}>{x}</li>)}</ul>
          <p className="gr-small gr-dim" data-part="verdict">{verdict}</p>
          <p className="v6-req-note">Fields marked * are required</p>
          <fieldset className="v6-fs" aria-describedby={appliesErr ? 'applies-err' : undefined}>
            <legend>Applies to<span aria-hidden="true" style={{ color: 'var(--danger-fg)' }}> *</span></legend>
            <label className="v6-radio"><input type="radio" name="applies" value="current" required checked={applies === 'current'} disabled={removed.length > 0}
              aria-invalid={appliesErr || undefined} aria-describedby={removed.length ? 'applies-cur-why' : undefined} onChange={() => { setApplies('current'); setAppliesErr(false); }} />
              <span>{`${open?.label ?? 'This'} window (in progress)`}<small>{`Record that ${firm} agreed · readiness changes now`}</small>
                {removed.length ? <small id="applies-cur-why">Not available: removing a category mid-window needs a new window</small> : null}</span></label>
            <label className="v6-radio"><input type="radio" name="applies" value="next" required checked={applies === 'next'} aria-invalid={appliesErr || undefined} onChange={() => { setApplies('next'); setAppliesErr(false); }} />
              <span>From the next window<small>{`${open?.label ?? 'The open window'} keeps its current scope`}</small></span></label>
            {appliesErr ? <span id="applies-err" className="v6-err">Choose which window this applies to</span> : null}
          </fieldset>
          {err ? <Alert variant="error"><p>{err}</p></Alert> : null}
          <div className="gr-actions">
            <ActButton variant="primary" compact onClick={() => void save()} aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Save scope'}</ActButton>
            <ActButton variant="ghost" compact onClick={cancel}>Cancel</ActButton>
          </div>
        </div>
      ) : null}
      {done ? (
        <div className="v6-result" data-part="scope-saved"><b><span aria-hidden="true">✓ </span>{done.msg}</b>
          <button ref={undoRef} type="button" className="gr-mutedlink" onClick={() => void undo()} disabled={busy}>Undo</button>
          {open ? <Link className="gr-textlink" to={`${prefix}/programs/soc2/prepare`}>Go to Prepare</Link> : null}</div>
      ) : null}
    </section>
  );
}

// ---- D6: the engine-switch acknowledgement ---------------------------------------------------

export function EngineSwitch({ slug, prefix, screen, announce, onSee, names }: {
  slug: string; prefix: string; screen: ProgramScreen; announce: (m: string) => void; onSee: (ids: string[]) => void; names: (id: string) => string;
}) {
  const qc = useQueryClient();
  const sw = screen.switch;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const t = today();
  // Aesthetic H (build review): a quiet info line; it never takes focus on load.
  const showCard = !!sw && !sw.acknowledged && sw.can_acknowledge;
  if (!sw) return null;
  const on = fmtDate(sw.approved_at.slice(0, 10), t);
  const moved = sw.changed.length;
  const ackLine = sw.acknowledged ? ` · acknowledged by ${names(sw.acknowledged.by)} · ${fmtDate(sw.acknowledged.at.slice(0, 10), t)}` : '';
  const numbers = sw.before && sw.after ? `${sw.before.criteria_met} → ${sw.after.criteria_met} criteria met` : null;
  if (!showCard) {
    if (t > sw.notice_until) return null;
    return (
      <p className="v6-notice" data-part="switch-notice" id="switch-notice" tabIndex={-1}>
        {`Readiness now counts each point of focus${numbers ? ` · ${numbers}` : ''} · ${on}${ackLine}`}
        {moved ? <button type="button" className="gr-mutedlink" onClick={() => onSee(sw.changed)}>See what changed</button> : null}
      </p>
    );
  }
  const ack = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      await recordProgramEntry(slug, 'soc2', { kind: 'engine_switch_ack', subject_key: sw.rule_set_id, data: {} });
      await qc.invalidateQueries({ queryKey: screenKey(slug, 'soc2') });
      announce(`Acknowledged · ${on}`);
      focusWhenReady(() => document.getElementById('switch-notice'), () => document.getElementById('crit-h') ?? document.getElementById('rd-h'));
    } catch (e) { setErr(errText(e, 'Could not record it.')); } finally { setBusy(false); }
  };
  return (
    <section className="v6-switch" aria-labelledby="ack-h" data-part="switch-ack">
      <h3 id="ack-h">{`Readiness now counts each point of focus · ${on}`}</h3>
      {sw.before && sw.after ? <span>{`Criteria met ${sw.before.criteria_met} → ${sw.after.criteria_met} of ${sw.after.criteria_total} · ${sw.after.points_covered} of ${sw.after.points_total} points covered${moved ? ` · ${moved} ${moved === 1 ? 'criterion' : 'criteria'} reopened` : ''}`}</span>
        : moved ? <span>{`${moved} ${moved === 1 ? 'criterion' : 'criteria'} reopened: a point inside each has no result yet`}</span> : null}
      {err ? <Alert variant="error"><p>{err}</p></Alert> : null}
      <div className="gr-actions">
        {moved ? <button type="button" className="gr-textlink v6-plain" onClick={() => onSee(sw.changed)}>{`See the ${moved} ${moved === 1 ? 'criterion' : 'criteria'}`}</button> : null}
        <ActButton compact onClick={() => void ack()} aria-disabled={busy || undefined}>Got it</ActButton>
        <Link className="gr-mutedlink" to={`${prefix}/queue?rule_set=soc2-readiness`}>Rule set and how to revert</Link>
      </div>
    </section>
  );
}

// ---- Prepare -----------------------------------------------------------------------------------

export function Soc2Prepare(props: Soc2PanelProps & { screen: ProgramScreen }) {
  const { slug, prefix, screen } = props;
  const [announceNode, announce] = useAnnounce();
  const { byId } = usePeople(slug);
  const [changed, setChanged] = useState<ReadonlySet<string> | null>(null);
  const open = openPeriodOf(screen);
  if (!open) return <p className="gp-tab-sub">No audit window yet · <Link to={props.tabHref('scope')}>Plan the window in Scope</Link></p>;
  const lead = screen.lead ? byId(screen.lead.user_id) : null;
  return <>
    {announceNode}
    <ObjectivePrepare slug={slug} prefix={prefix} program="soc2" periodId={open.period_id} periodLabel={open.label} hardDate={{ label: 'Window ends', date: open.end }}
      role={props.role} canManage={props.canManage} isAdmin={props.isAdmin} viewerId={props.viewerId} lead={lead} announce={announce}
      changedOnly={changed} onClearChanged={() => setChanged(null)}
      lead_in={<EngineSwitch slug={slug} prefix={prefix} screen={screen} announce={announce} names={(id) => byId(id)?.name ?? 'a teammate'}
        onSee={(ids) => { setChanged(new Set(ids)); announce(`Showing ${ids.length} criteria changed by the new counting`); window.setTimeout(() => document.querySelector<HTMLElement>('[data-part="changed-chip"] button')?.focus(), 0); }} />} />
  </>;
}

// ---- Audit ---------------------------------------------------------------------------------

export function Soc2Audit(props: Soc2PanelProps & { screen: ProgramScreen }) {
  const { slug, prefix, screen, canManage } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [announceNode, announce] = useAnnounce();
  const { byId } = usePeople(slug);
  const t = today();
  const qs = new URLSearchParams(location.search);
  const periods = [...screen.periods].sort((a, b) => b.end.localeCompare(a.end));
  const needsAttach = periods.find((p) => p.end < t && p.soc2?.issued && !p.soc2.report);
  const openP = openPeriodOf(screen);
  const want = qs.get('period');
  const [sel, setSel] = useState<string | null>(() => (want && periods.some((p) => p.period_id === want) ? want : needsAttach?.period_id ?? openP?.period_id ?? periods[0]?.period_id ?? null));
  const attachRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (location.hash === '#attach') window.setTimeout(() => attachRef.current?.querySelector<HTMLInputElement>('input[type="file"]')?.focus(), 30);
  }, [location.hash]);
  if (!periods.length) return <p className="gp-tab-sub">No audit window yet · <Link to={props.tabHref('scope')}>Plan the window in Scope</Link></p>;
  const p = periods.find((x) => x.period_id === sel) ?? periods[0]!;
  const lead = screen.lead ? byId(screen.lead.user_id) : null;
  const tag = (x: typeof p) => (x.end >= t ? (x === openP ? 'next audit' : 'planned') : x.soc2?.report ? 'report on file' : x.soc2?.issued ? 'report not on file' : x.soc2?.draft_sent ? 'draft sent' : 'report not recorded');
  return (
    <div className="gr-page v6-scope" data-part="soc2-audit">
      {announceNode}
      {periods.length > 1 ? (
        <div className="v6-period">
          <span className="gr-small gr-dim" id="per-l">Window</span>
          <div className="gr-seg-filter" role="group" aria-labelledby="per-l">
            {periods.map((x) => <button key={x.period_id} type="button" aria-pressed={x.period_id === p.period_id}
              onClick={() => { setSel(x.period_id); navigate({ search: `?period=${x.period_id}` }, { replace: true }); announce(`Showing the ${x.label} window`); }}>{`${x.label} · ${tag(x)}`}</button>)}
          </div>
        </div>
      ) : null}
      {p.end >= t ? <OpenWindow {...props} p={p} lead={lead} announce={announce} /> : <EndedWindow {...props} p={p} lead={lead} announce={announce} attachRef={attachRef} />}
      {!canManage && !lead ? null : null}
      <ul className="gp-links" aria-label="Audit"><li><Link to={`${prefix}/settings/audit-firms`}>Your CPA firm</Link></li><li><Link to={`${prefix}/calendar`}>Calendar & trust center</Link></li></ul>
    </div>
  );
}

type PeriodView = ProgramScreen['periods'][number];

function OpenWindow({ slug, prefix, screen, canManage, p, lead, announce, viewerId, tabHref }: Soc2PanelProps & { screen: ProgramScreen; p: PeriodView; lead: Person | null; announce: (m: string) => void }) {
  const qc = useQueryClient();
  const t = today();
  const exp = p.soc2?.expected ?? null;
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(exp?.date ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { byId } = usePeople(slug);
  const readiness = useQuery({ queryKey: queryKeys.objectiveReadiness(slug, 'soc2', p.period_id), queryFn: () => getObjectiveReadiness(slug, 'soc2', p.period_id), retry: false, staleTime: 60_000 });
  const open = readiness.data ? readiness.data.readiness.requirements.flatMap((r) => r.objectives).filter((o) => o.status !== 'met' && o.status !== 'not_applicable').length : null;
  const firm = p.soc2?.firm_name ?? null;
  const save = async () => {
    if (busy) return;
    if (!date) { setErr('Choose the expected report date'); return; }
    setBusy(true); setErr(null);
    try { await recordProgramEntry(slug, 'soc2', { kind: 'expected_report_date', subject_key: p.period_id, data: { date } }); await qc.invalidateQueries({ queryKey: screenKey(slug, 'soc2') }); setEditing(false); announce(`Expected report date ${fmtDate(date, t)}`); focusWhenReady(() => document.getElementById('exp-change')); }
    catch (e) { setErr(errText(e, 'Could not save the date.')); } finally { setBusy(false); }
  };
  return (
    <section className="gr-status" data-tone="gate" aria-labelledby="win-h">
      <h3 id="win-h" className="gr-hero-label">{`${p.label} window · ${p.report_type === 'type1' ? 'Type I' : 'Type II'} · ${fmtDate(p.start, '0000')} – ${fmtDate(p.end, '0000')}`}</h3>
      <div className="gr-status-grid">
        <div className="gr-stack">
          <p className="gr-status-line v6-sm"><Glyph status="ns" /><span className="v6-t">{firm ? `Fieldwork after the window · ${firm}` : 'No CPA firm invited yet'}</span></p>
          {!firm ? <p className="gr-small"><Link className="gr-textlink" to={`${prefix}/settings/audit-firms`}>Invite your CPA firm</Link> · or record their report here once it’s issued</p> : null}
          <p className="gr-subline">
            <span>{exp ? <>Expected report <b className="gr-strong">{fmtDate(exp.date, t)}</b>{` · entered by ${byId(exp.recorded_by)?.name ?? 'your team'} on ${fmtDate(exp.recorded_at.slice(0, 10), t)}`}</> : 'Expected report date not set'}</span>
            {canManage ? <button type="button" id="exp-change" className="gr-mutedlink" aria-expanded={editing} aria-controls="exp-edit" onClick={() => setEditing(!editing)}>{exp ? 'Change' : 'Set the date'}</button>
              : lead && lead.user_id !== viewerId ? <AskButton slug={slug} program="soc2" person={lead} about={`Set the expected ${p.label} report date`} announce={announce} /> : null}
          </p>
          {editing ? (
            <div className="gr-recform" id="exp-edit">
              <Field id="exp-d" label="Expected report date" error={err ?? undefined} errorRole="none"><Input type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} /></Field>
              <span className="gr-actions"><ActButton compact onClick={() => void save()} aria-disabled={busy || undefined}>Save</ActButton></span>
            </div>
          ) : null}
        </div>
        <Datebox label="Window ends" date={fmtDate(p.end, t)} note={`In ${relDays(p.end, t)}`} />
      </div>
      <ul className="gr-flat">
        <li><span>{open === null ? 'Prepare' : `Prepare · ${open} points open`}</span><Link className="gr-textlink gr-small" to={tabHref('prepare')}>Go to Prepare</Link></li>
        {screen.periods.length ? <li><span>Auditor requests open when the window ends</span><Link className="gr-textlink gr-small" to={`${prefix}/evidence-requests`}>Auditor requests</Link></li> : null}
      </ul>
    </section>
  );
}

function EndedWindow({ slug, prefix, screen, canManage, p, lead, announce, viewerId, attachRef, tabHref }: Soc2PanelProps & { screen: ProgramScreen; p: PeriodView; lead: Person | null; announce: (m: string) => void; attachRef: React.RefObject<HTMLDivElement> }) {
  const qc = useQueryClient();
  const t = today();
  const s = p.soc2!;
  const { byId } = usePeople(slug);
  const classA = screen.class_a;
  const [file, setFile] = useState<File | null>(null);
  const [opinion, setOpinion] = useState('');
  const [rdate, setRdate] = useState('');
  const [errs, setErrs] = useState<{ file?: string; opinion?: string; date?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [justAttached, setJustAttached] = useState<{ until: string | null; schedule: boolean } | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const firm = s.issued?.firm_name ?? s.firm_name ?? 'the CPA firm';
  const offPlatform = !s.issued && !s.draft_sent;
  const attach = async () => {
    if (busy) return;
    const e: typeof errs = {};
    if (!file) e.file = 'Choose the report PDF';
    if (offPlatform && !opinion) e.opinion = 'Choose the opinion on the report';
    if (offPlatform && !rdate) e.date = 'Enter the report date';
    setErrs(e);
    if (e.file || e.opinion || e.date) {
      announce(e.file ?? e.opinion ?? e.date!);
      document.getElementById(e.file ? 'attach' : e.opinion ? 'oo-op' : 'oo-d')?.focus();
      return;
    }
    setBusy(true);
    try {
      const r = await uploadSoc2Report(slug, p.period_id, { file: file!, ...(offPlatform ? { opinion_type: opinion, report_date: rdate } : {}) });
      const until = r.linked_materials[0]?.expires_at?.slice(0, 10) ?? null;
      setJustAttached({ until, schedule: !!classA && !classA.schedule });
      announce(`Report attached${until ? ` · counts for Class A until ${fmtDate(until, t)}` : ''}`);
      await qc.invalidateQueries({ queryKey: screenKey(slug, 'soc2') });
      void qc.invalidateQueries({ queryKey: ['today-programs', slug] });
      focusWhenReady(() => resultRef.current);
    } catch (x) { setErrs({ form: errText(x, 'The report could not be attached.') }); } finally { setBusy(false); }
  };
  const dets = useQuery({ queryKey: queryKeys.determinations(slug, 'soc2', p.period_id, 'all'), queryFn: () => listDeterminations(slug, 'soc2', p.period_id), retry: false, staleTime: 60_000 });
  const cpa = (dets.data?.determinations ?? []).filter((d) => d.kind === 'assessor' && d.firm_type === 'cpa' && d.state === 'reviewed');
  const label = (id: string) => { const m = /^soc2:(.*)_obj\.(\d+)$/.exec(id); return m ? `${m[1]}-POF-${m[2]!.padStart(2, '0')}` : id; };
  const resultCard = cpa.length ? (
    <section className="gr-card" aria-labelledby="cpa-h" data-part="cpa-results">
      <div className="gr-card-head"><h3 id="cpa-h" className="gr-card-h">{`Results from ${firm} · ${p.label}`}</h3><span className="gp-chip gp-chip--lock">Read only</span></div>
      <p className="gr-hero-fig"><b>{cpa.filter((d) => d.result === 'met').length}</b><span className="gr-dim">met</span><b>{cpa.filter((d) => d.result === 'not_met').length}</b><span className="gr-dim">exceptions</span><b>{cpa.filter((d) => d.result === 'not_applicable').length}</b><span className="gr-dim">not applicable</span></p>
      {cpa.some((d) => d.result === 'not_met') ? <>
        <h4 className="gr-sub-h">{`Exceptions · ${cpa.filter((d) => d.result === 'not_met').length}`}</h4>
        <ul className="gr-flat" aria-label="Exceptions">
          {cpa.filter((d) => d.result === 'not_met').map((d) => (
            <li key={d.determination_id} className="gr-ck"><Glyph status="act" /><span className="gr-ck-t">{d.rationale ?? 'Exception'}</span>
              <span className="gr-ck-m">{'Exception · '}<span className="v6-id">{label(d.objective_id)}</span></span>
              <span className="gr-ck-a"><Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/soc2/prepare#${objectiveAnchor('soc2', label(d.objective_id))}`}>Plan a fix<span className="gp-sr-only">{` ${label(d.objective_id)}`}</span></Link></span></li>
          ))}
        </ul></> : null}
    </section>
  ) : null;

  if (s.report || justAttached) {
    const r = s.report;
    const op = r?.opinion_type ?? 'unmodified';
    const good = op === 'unmodified';
    const until = justAttached?.until ?? (classA?.report?.report_period_end === p.end ? classA.report.expires_at : null);
    return <>
      {justAttached ? (
        <div className="v6-result" ref={resultRef} tabIndex={-1} data-part="attached" style={{ display: 'grid' }}>
          <b><span aria-hidden="true">✓ </span>{`Report attached${until ? ` · counts for Class A until ${fmtDate(until, t)}` : ''}`}</b>
          {justAttached.schedule ? <><span>1 item still missing: the audit schedule</span><ScheduleUpload slug={slug} screen={screen} announce={announce} lead={lead} viewerId={viewerId} canUpload={screen.viewer.is_admin} /></> : null}
        </div>
      ) : null}
      {r ? (
        <section className="gr-status" data-tone={good ? 'ok' : 'bad'} aria-labelledby="rep-h">
          <h3 id="rep-h" className="gr-hero-label">{`${p.label} report · ${p.report_type === 'type1' ? 'Type I' : 'Type II'} · ${fmtDate(p.start, '0000')} – ${fmtDate(p.end, '0000')}`}</h3>
          <div className="gr-status-grid">
            <div className="gr-stack">
              <p className="gr-status-line"><Glyph status={good ? 'done' : 'not'} /><span className="v6-t">{good ? 'Report on file · Unmodified opinion' : `${OPINION[op]} opinion`}</span></p>
              <p className="v6-attr"><span className="v6-lock" aria-hidden="true" />{r.opinion_source === 'firm' ? <>Opinion and date from <b>{firm}</b></> : <>Entered by your team</>}{` · PDF attached by ${byId(r.recorded_by)?.name ?? 'your team'}, ${fmtDate(r.recorded_at.slice(0, 10), t)}`}</p>
              {!good ? <p className="gr-consequence">Doesn’t count for FedRAMP Class A</p> : null}
            </div>
            <Datebox label="Report date" date={fmtDate(r.report_date, '0000')} note={until ? `Class A until ${fmtDate(until, t)}` : relDays(r.report_date, t)} />
          </div>
          <div className="gr-actions">
            {!good ? <Link className="gp-linkbtn btn-primary" to={`${prefix}/findings?program=soc2`}>Plan the fixes</Link> : null}
            {classA ? <Link className="gr-textlink gr-small" to={`${tabHref('operate')}#class-a`}>FedRAMP Class A</Link> : null}
          </div>
        </section>
      ) : null}
      {resultCard}
    </>;
  }
  // Issued (or off-platform) but not on file: attach the PDF.
  const canAttach = canManage;
  return <>
    <section className="gr-status" data-tone={s.issued ? 'warn' : 'gate'} aria-labelledby="un-h" data-part="report-unattached">
      <h3 id="un-h" className="gr-hero-label">{`${p.label} report · ${p.report_type === 'type1' ? 'Type I' : 'Type II'} · ${fmtDate(p.start, '0000')} – ${fmtDate(p.end, '0000')}`}</h3>
      <div className="gr-stack">
        {s.draft_sent && !s.issued ? <>
          <p className="gr-status-line v6-sm"><Glyph status="act" /><span className="v6-t">{`Draft report sent · ${s.draft_sent.firm_name ?? firm}`}</span></p>
          <p className="gr-subline">The opinion shows here when the report is issued</p>
        </> : s.issued ? <>
          <p className="gr-status-line v6-sm"><Glyph status="act" /><span className="v6-t">Report not on file · attach the PDF</span></p>
          <p className="v6-attr"><span className="v6-lock" aria-hidden="true" /><b>{firm}</b>{` · ${OPINION[s.issued.opinion_type ?? ''] ?? 'Issued'}${s.issued.report_date ? ` · issued ${fmtDate(s.issued.report_date, t)} · ${relDays(s.issued.report_date, t)}` : ''}`}</p>
          {classA ? <p className="gr-consequence">Blocks FedRAMP Class A until it’s attached</p> : null}
        </> : <>
          <p className="gr-status-line v6-sm"><Glyph status="ns" /><span className="v6-t">Record the issued report</span></p>
          <p className="gr-small gr-dim">Your CPA works outside GAC Pro · shown as “entered by your team”</p>
        </>}
      </div>
      {s.draft_sent && !s.issued ? <div className="gr-actions"><Link className="gp-linkbtn" to={`${prefix}/evidence-requests`}>Answer auditor requests</Link></div> : (
        <div ref={attachRef}>
          {offPlatform ? <p className="v6-req-note">Fields marked * are required</p> : null}
          <div className={offPlatform ? 'v6-form' : ''}>
            {offPlatform ? (
              <div className="v6-row">
                <Field id="oo-op" label="Opinion" required error={errs.opinion} errorRole="none"><Select value={opinion} disabled={!canAttach} onChange={(e) => setOpinion(e.currentTarget.value)}>
                  <option value="">Choose…</option>{Object.entries(OPINION).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
                <Field id="oo-d" label="Report date" required error={errs.date} errorRole="none"><Input type="date" value={rdate} disabled={!canAttach} onChange={(e) => setRdate(e.currentTarget.value)} /></Field>
              </div>
            ) : null}
            <div className="v6-upload">
              <FileField id="attach" label="Report PDF" required accept="application/pdf" error={errs.file} disabled={!canAttach} describedBy={!canAttach ? 'att-g' : undefined}
                onFile={(f) => { setFile(f); setErrs((x) => ({ ...x, file: undefined })); }} />
              <ActButton variant="primary" onClick={() => canAttach && void attach()} aria-disabled={!canAttach || busy || undefined} aria-describedby={!canAttach ? 'att-g' : undefined}>{busy ? 'Attaching…' : offPlatform ? 'Record' : 'Attach report'}</ActButton>
            </div>
          </div>
          {errs.form ? <Alert variant="error"><p>{errs.form}</p></Alert> : null}
          {!canAttach ? <Gate id="att-g" who="The SOC 2 lead or an admin" verb="attaches it"
            ask={lead && lead.user_id !== viewerId ? <AskButton slug={slug} program="soc2" person={lead} about={`Attach the ${p.label} SOC 2 report`} announce={announce} /> : null} /> : null}
        </div>
      )}
    </section>
    {resultCard}
  </>;
}


// ---- Operate: Class A material from SOC 2 ---------------------------------------------------

function ScheduleUpload({ slug, screen, announce, lead, viewerId, canUpload }: { slug: string; screen: ProgramScreen; announce: (m: string) => void; lead: Person | null; viewerId: string; canUpload: boolean }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);
  const ca = screen.class_a!;
  if (done) return <p className="v6-result" ref={ref} tabIndex={-1} data-part="schedule-done"><b><span aria-hidden="true">✓ </span>Schedule added · Class A material complete</b></p>;
  if (!canUpload) return <Gate id="sch-g" who="An admin or the FedRAMP lead" verb="uploads this" ask={lead && lead.user_id !== viewerId ? <AskButton slug={slug} program="soc2" person={lead} about="Upload the CPA audit schedule for Class A" announce={announce} /> : null} />;
  const go = async () => {
    if (busy) return;
    if (!file) { setErr('Choose the schedule PDF'); document.getElementById('sch-f')?.focus(); return; }
    setBusy(true); setErr(null);
    try {
      await uploadAlternativeMaterial(slug, ca.profile_id, { file, framework: 'soc2_type2', kind: 'schedule', assessedAt: ca.report?.report_period_end?.slice(0, 10) ?? today(), title: file.name });
      setDone(true); announce('Schedule added · Class A material complete');
      void qc.invalidateQueries({ queryKey: screenKey(slug, 'soc2') });
      focusWhenReady(() => ref.current ?? document.getElementById('sch-status'));
    } catch (e) { setErr(errText(e, 'The schedule could not be uploaded.')); } finally { setBusy(false); }
  };
  return (
    <div className="v6-upload" data-part="schedule-upload">
      <FileField id="sch-f" label="Audit schedule from the CPA" required error={err} onFile={setFile} />
      <ActButton onClick={() => void go()} aria-disabled={busy || undefined}>Upload schedule</ActButton>
    </div>
  );
}

export function Soc2ClassA({ slug, prefix, screen, viewerId, tabHref }: Soc2PanelProps & { screen: ProgramScreen }) {
  const [announceNode, announce] = useAnnounce();
  const { byId } = usePeople(slug);
  const [uploadOpen, setUploadOpen] = useState(false);
  const ca = screen.class_a;
  // Flow H (round 2): Today's "#class-a" deep link lands on this section.
  useEffect(() => { if (window.location.hash === '#class-a') focusWhenReady(() => document.getElementById('ca-h')); }, []);
  if (!ca) return null;
  const t = today();
  const lead = screen.lead ? byId(screen.lead.user_id) : null;
  const r = ca.report;
  const days = r ? dayDiff(t, r.expires_at) : null;
  const opinionBad = !!r?.opinion_type && r.opinion_type !== 'unmodified';
  const missing = !ca.schedule ? 1 : 0;
  const nextExpected = openPeriodOf(screen)?.soc2?.expected?.date ?? null;
  const state = !r ? 'none' : opinionBad ? 'opinion' : days! < 0 ? 'expired' : days! <= 45 ? 'expiring' : missing ? 'missing' : 'complete';
  const tone = state === 'complete' ? 'ok' : state === 'expired' || state === 'opinion' ? 'bad' : 'warn';
  const head = {
    none: 'No SOC 2 Type II report linked yet', complete: 'Counts for Class A', missing: 'Report linked · 1 item missing',
    expiring: `Expires in ${days} ${days === 1 ? 'day' : 'days'}`, expired: 'Expired · Class A finding open', opinion: `${OPINION[r?.opinion_type ?? ''] ?? 'Qualified'} opinion · doesn’t count`,
  }[state];
  return (
    <section className="gr-status" data-tone={tone} aria-labelledby="ca-h" id="class-a" data-part="soc2-class-a" data-state={state}>
      {announceNode}
      <h3 id="ca-h" className="gr-hero-label" tabIndex={-1}>FedRAMP 20x Class A · SOC 2 material</h3>
      <div className="gr-status-grid">
        <div className="gr-stack">
          <p className="gr-status-line v6-sm"><Glyph status={tone === 'ok' ? 'done' : tone === 'bad' ? 'not' : 'act'} /><span className="v6-t">{head}</span></p>
          {state === 'expired' ? <p className="gr-consequence">{nextExpected ? `No Class A until ${fmtDate(nextExpected, t)} · ${relDays(nextExpected, t)}` : 'No Class A until a new report is attached'}</p> : null}
          {state === 'expiring' ? <p className="gr-subline">{nextExpected ? `${Math.max(0, dayDiff(r!.expires_at, nextExpected))}-day gap before the next report (expected ${fmtDate(nextExpected, t)})` : 'The next report’s date isn’t set'}</p> : null}
          {state === 'opinion' ? <p className="gr-consequence">Class A finding open until an unmodified report is attached</p> : null}
          {r && state !== 'expired' && state !== 'opinion' ? <p className="gr-subline">{`${r.report_period_end ? `Period end ${fmtDate(r.report_period_end, t)}` : 'SOC 2 Type II'} · ${OPINION[r.opinion_type ?? 'unmodified'] ?? 'Unmodified'} · linked when the PDF was attached`}</p> : null}
        </div>
        {r ? <Datebox label={state === 'expired' ? 'Expired' : 'Counts until'} date={fmtDate(r.expires_at, '0000')} note={state === 'expired' ? relDays(r.expires_at, t) : `${relDays(r.expires_at, t)} · 12 months from period end`} late={state === 'expired'} /> : null}
      </div>
      <ul className="gr-flat" aria-label="Class A SOC 2 items">
        <li><span><Glyph status={r ? 'done' : 'noev'} />{' SOC 2 Type II report'}</span>{r ? <span className="gr-small gr-dim">Linked automatically</span> : <Link className="gp-linkbtn gp-linkbtn--compact" to={`${tabHref('audit')}?attach=1#attach`}>Attach the report</Link>}</li>
        <li><span><Glyph status={ca.engagement_letter ? 'done' : 'noev'} />{' Engagement letter'}</span>{ca.engagement_letter ? <span className="gr-small gr-dim">Uploaded</span> : <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/fedramp-cr26/prepare`}>Upload in Class A</Link>}</li>
        <li style={{ flexWrap: 'wrap' }}><span><Glyph status={ca.schedule ? 'done' : 'noev'} />{' '}<b className="gr-strong">Audit schedule from the CPA</b></span>
          {ca.schedule ? <span className="gr-small gr-dim" id="sch-status" tabIndex={-1}>Uploaded</span>
            : <button type="button" className={`gp-linkbtn gp-linkbtn--compact${state === 'missing' ? ' btn-primary' : ''}`} aria-expanded={uploadOpen} aria-controls="sch-panel" onClick={() => setUploadOpen(!uploadOpen)}>Upload schedule</button>}
          {uploadOpen && !ca.schedule ? <div id="sch-panel" style={{ flex: '1 1 100%' }}><ScheduleUpload slug={slug} screen={screen} announce={announce} lead={lead} viewerId={viewerId} canUpload={screen.viewer.is_admin} /></div> : null}
        </li>
      </ul>
      {state === 'expired' || state === 'expiring' ? (
        <div className="gr-actions">
          <Link className="gp-linkbtn btn-primary" to={`${tabHref('audit')}`}>{state === 'expired' ? 'Plan the next report' : 'Set the expected report date'}</Link>
          {state === 'expired' ? <Link className="gr-mutedlink" to={`${prefix}/findings?program=fedramp-cr26`}>The finding</Link> : null}
        </div>
      ) : state === 'opinion' ? <div className="gr-actions"><Link className="gp-linkbtn btn-primary" to={`${prefix}/findings?program=soc2`}>Plan the fixes</Link></div> : null}
      <details className="gr-why"><summary>Where this comes from</summary><div>
        <span>FedRAMP 20x Class A accepts a SOC 2 Type II completed in the last 12 months (FRC-CLA-ASF), with the engagement letter and schedule (FRC-CLA-EAM).</span>
        <span>A lapse, an expiry or an opinion other than unmodified opens a Class A finding (IVV-CSX-AIA; the opinion rule is GAC Pro’s reading).</span>
      </div></details>
    </section>
  );
}

export function ScreenLoading({ q, label }: { q: { isLoading: boolean; error: unknown; refetch: () => unknown }; label: string }) {
  if (q.isLoading) return <Spinner label={`Loading ${label}`} />;
  return <Alert variant="error"><p>{`${label} could not load.`}</p><ActButton onClick={() => void q.refetch()}>Retry</ActButton></Alert>;
}

export { firstName };
